import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cloud, RefreshCw, Loader2, Link2, Plus, Copy, Check, RotateCcw, Trash2, ExternalLink,
  FileText, AlertTriangle, CheckCircle2, Scissors,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import * as api from '@/services/transporterPortalApi';
import { AutoPushSummary } from '@/components/ewayBill/AutoPushSummary';

const TransporterPortalAdmin = () => {
  // ---- DC invoice sync state ----
  const [status, setStatus] = useState(null);
  const [folderInput, setFolderInput] = useState('');
  const [showFolderEdit, setShowFolderEdit] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // ---- bulk invoice PDF split-upload state ----
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = useRef(null);

  // ---- portal links state ----
  const [links, setLinks] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');

  const loadStatus = useCallback(async () => {
    try { setStatus(await api.getDcInvoiceStatus()); } catch { /* non-fatal */ }
  }, []);

  const loadLinks = useCallback(async () => {
    try { const d = await api.listPortalLinks(); setLinks(d.links || []); } catch { /* non-fatal */ }
  }, []);

  const loadTransporters = useCallback(async () => {
    try { const d = await api.listPortalTransporters(); setTransporters(d.transporters || []); } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadStatus(); loadLinks(); loadTransporters(); }, [loadStatus, loadLinks, loadTransporters]);

  const handleSaveFolder = async () => {
    if (!folderInput.trim()) { toast.error('Paste a Google Drive folder link or ID'); return; }
    setSavingFolder(true);
    try {
      await api.setDcInvoiceFolder(folderInput.trim());
      toast.success('Drive folder saved');
      setShowFolderEdit(false); setFolderInput('');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Drive folder');
    } finally { setSavingFolder(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const d = await api.triggerDcInvoiceSync();
      toast.success(`Synced: ${d.matched || 0} matched · ${d.downloaded || 0} downloaded · ${d.skipped || 0} unchanged`);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const handleBulkUpload = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { toast.error('Please choose a PDF file'); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const d = await api.uploadBulkDcInvoices(file);
      setBulkResult(d);
      toast.success(`Split ${d.total_invoices} invoice(s): ${d.matched} matched · ${d.unmatched} unmatched · ${d.error_count} error(s)`);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    }
  };

  const handleCreateLink = async () => {
    if (!selectedTransporter) { toast.error('Select a transporter'); return; }
    setCreating(true);
    try {
      await api.createPortalLink(selectedTransporter);
      toast.success(`Portal link created for ${selectedTransporter}`);
      setSelectedTransporter('');
      loadLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create link');
    } finally { setCreating(false); }
  };

  const handleRegenerate = async (link) => {
    if (!window.confirm(`Regenerate passcode & link for "${link.transport_name}"? The old link will stop working.`)) return;
    try {
      await api.regeneratePortalLink(link.link_id);
      toast.success('Link & passcode regenerated');
      loadLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleToggle = async (link, active) => {
    try {
      await api.togglePortalLink(link.link_id, active);
      setLinks((prev) => prev.map((l) => (l.link_id === link.link_id ? { ...l, active } : l)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete the portal link for "${link.transport_name}"?`)) return;
    try {
      await api.deletePortalLink(link.link_id);
      toast.success('Link deleted');
      loadLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const portalUrl = (token) => `${window.location.origin}/transporter-portal/${token}`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(''), 1500);
    }).catch(() => toast.error('Copy failed'));
  };

  const stats = status?.last_sync_stats;

  return (
    <>
      {/* ============ DC Invoice Auto-Sync ============ */}
      <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 mt-6" data-testid="dc-invoice-sync-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
              <Cloud className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#1A1F2E] font-display">DC Invoice Auto-Sync (Google Drive)</h3>
              <p className="text-sm text-gray-500">
                PDF invoices named by DC number are pulled from a shared Drive folder every 30&nbsp;min and linked to each consignment.
              </p>
            </div>
          </div>
          <Button
            onClick={handleSync}
            disabled={syncing || !status?.folder_configured || !status?.api_key_configured}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="dc-invoice-sync-now-btn"
          >
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="w-4 h-4 mr-2" />Sync Now</>}
          </Button>
        </div>

        {/* Status chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="dc-invoice-status">
          <div className="p-3 rounded-lg bg-[#F5F1E8] text-center">
            <p className="text-xs text-gray-500">Drive Folder</p>
            <Badge className={status?.folder_configured ? 'bg-green-100 text-green-800 mt-1' : 'bg-amber-100 text-amber-800 mt-1'}>
              {status?.folder_configured ? 'Configured' : 'Not set'}
            </Badge>
          </div>
          <div className="p-3 rounded-lg bg-[#F5F1E8] text-center">
            <p className="text-xs text-gray-500">Drive API Key</p>
            <Badge className={status?.api_key_configured ? 'bg-green-100 text-green-800 mt-1' : 'bg-red-100 text-red-800 mt-1'}>
              {status?.api_key_configured ? 'Active' : 'Missing'}
            </Badge>
          </div>
          <div className="p-3 rounded-lg bg-[#F5F1E8] text-center">
            <p className="text-xs text-gray-500">Invoices Stored</p>
            <p className="text-xl font-bold text-[#1A1F2E]" data-testid="dc-invoice-total">{status?.total_invoices ?? 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#F5F1E8] text-center">
            <p className="text-xs text-gray-500">Matched to DC</p>
            <p className="text-xl font-bold text-[#2D5F4F]" data-testid="dc-invoice-matched">{status?.matched_invoices ?? 0}</p>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 text-sm text-gray-600 mb-3">
          <span>
            {status?.last_sync
              ? <>Last sync: <b>{new Date(status.last_sync).toLocaleString()}</b>{stats ? ` · listed ${stats.listed} · downloaded ${stats.downloaded} · unmatched ${stats.unmatched_count}` : ''}</>
              : 'Not synced yet'}
          </span>
          <Button variant="outline" size="sm" onClick={() => { setShowFolderEdit((v) => !v); setFolderInput(''); }} data-testid="dc-invoice-change-folder-btn">
            {status?.folder_configured ? 'Change folder link' : 'Set folder link'}
          </Button>
        </div>

        {showFolderEdit && (
          <div className="pt-3 border-t border-[#E5DFD0] space-y-2" data-testid="dc-invoice-folder-panel">
            <Label className="text-sm">Google Drive folder link or ID</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={folderInput}
                onChange={(e) => setFolderInput(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/FOLDER_ID — or just the folder ID"
                data-testid="dc-invoice-folder-input"
              />
              <Button onClick={handleSaveFolder} disabled={savingFolder || !folderInput.trim()} className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white whitespace-nowrap" data-testid="dc-invoice-folder-save-btn">
                {savingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Folder'}
              </Button>
            </div>
            <p className="text-[11px] text-gray-500">
              Share the folder as &ldquo;Anyone with the link &rarr; Viewer&rdquo;. Name each PDF by its DC number (e.g. <code>DC-41452-26-27.pdf</code>).
            </p>
          </div>
        )}

        {/* ---- Bulk invoice PDF: auto-split by DC number ---- */}
        <div className="mt-6 pt-5 border-t border-[#E5DFD0]" data-testid="dc-bulk-upload-panel">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
              <Scissors className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="font-medium text-[#1A1F2E]">Split a bulk invoice PDF</p>
              <p className="text-xs text-gray-500">
                Upload one PDF containing many tax invoices. Each invoice is auto-detected by its <b>Invoice No</b>,
                tagged to its <b>Challan No&nbsp;: DC-…</b>, split out, and linked to its pick slip. Each invoice is then sent to Tally
                as a Sales voucher with the transporter and distance filled in (held ones go to E-Way Bills → Needs attention).
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <input
              ref={bulkFileRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => handleBulkUpload(e.target.files)}
              disabled={bulkUploading}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white file:cursor-pointer hover:file:bg-violet-700 disabled:opacity-50"
              data-testid="dc-bulk-file-input"
            />
            {bulkUploading && (
              <span className="flex items-center text-sm text-violet-700 whitespace-nowrap" data-testid="dc-bulk-uploading">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />Splitting &amp; linking…
              </span>
            )}
          </div>

          {status?.last_bulk_upload && !bulkResult && (
            <p className="text-[11px] text-gray-500 mt-2" data-testid="dc-bulk-last">
              Last bulk upload: <b>{new Date(status.last_bulk_upload).toLocaleString()}</b>
              {status.last_bulk_stats ? ` · ${status.last_bulk_stats.invoices} invoices · ${status.last_bulk_stats.matched} matched · ${status.last_bulk_stats.errors} error(s)` : ''}
            </p>
          )}

          {bulkResult && (
            <div className="mt-4 space-y-3" data-testid="dc-bulk-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 rounded-lg bg-[#F5F1E8] text-center">
                  <p className="text-[11px] text-gray-500">Invoices</p>
                  <p className="text-lg font-bold text-[#1A1F2E]" data-testid="dc-bulk-total">{bulkResult.total_invoices}</p>
                </div>
                <div className="p-2 rounded-lg bg-green-50 text-center">
                  <p className="text-[11px] text-gray-500">Matched</p>
                  <p className="text-lg font-bold text-[#2D5F4F]" data-testid="dc-bulk-matched">{bulkResult.matched}</p>
                </div>
                <div className="p-2 rounded-lg bg-amber-50 text-center">
                  <p className="text-[11px] text-gray-500">Unmatched</p>
                  <p className="text-lg font-bold text-amber-700" data-testid="dc-bulk-unmatched">{bulkResult.unmatched}</p>
                </div>
                <div className="p-2 rounded-lg bg-red-50 text-center">
                  <p className="text-[11px] text-gray-500">Errors</p>
                  <p className="text-lg font-bold text-red-600" data-testid="dc-bulk-errors">{bulkResult.error_count}</p>
                </div>
              </div>

              <div className="rounded-md border overflow-x-auto max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">DC Number</TableHead>
                      <TableHead className="font-semibold">Invoice No</TableHead>
                      <TableHead className="font-semibold text-center">Pages</TableHead>
                      <TableHead className="font-semibold text-center">Pick Slip</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(bulkResult.invoices || []).map((inv, i) => (
                      <TableRow key={inv.dc_number || i} data-testid={`dc-bulk-row-${inv.dc_number || i}`}>
                        <TableCell className="font-mono text-sm flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5 text-violet-500" />{inv.dc_number}
                          {inv.warning && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title={inv.warning} />}
                        </TableCell>
                        <TableCell className="text-sm">{inv.invoice_no}</TableCell>
                        <TableCell className="text-center text-sm">{inv.page_count}</TableCell>
                        <TableCell className="text-center">
                          {inv.matched
                            ? <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" />{inv.pick_slip_number}</Badge>
                            : <Badge className="bg-amber-100 text-amber-800">No pick slip</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {(bulkResult.errors || []).length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="dc-bulk-error-list">
                  <p className="text-sm font-medium text-red-700 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />Skipped ({bulkResult.errors.length})
                  </p>
                  <ul className="text-xs text-red-600 list-disc pl-5 space-y-0.5">
                    {bulkResult.errors.map((er, i) => (
                      <li key={i}>{er.invoice_no || '(no invoice)'} — pages {(er.pages || []).join(', ')} — {er.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <AutoPushSummary runId={bulkResult.auto_push?.id} />
            </div>
          )}
        </div>
      </div>

      {/* ============ Transporter Portal Links ============ */}
      <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 mt-6" data-testid="portal-links-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Link2 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1A1F2E] font-display">Transporter Portal Links</h3>
            <p className="text-sm text-gray-500">Generate a passcode-protected link per transporter to view their consignments &amp; download invoices.</p>
          </div>
        </div>

        {/* Create link */}
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <Select value={selectedTransporter} onValueChange={setSelectedTransporter}>
            <SelectTrigger className="flex-1" data-testid="portal-transporter-select">
              <SelectValue placeholder="Select a transporter..." />
            </SelectTrigger>
            <SelectContent>
              {transporters.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-400">No transporters found</div>
              ) : transporters.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleCreateLink} disabled={creating || !selectedTransporter} className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap" data-testid="portal-create-link-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Create Link
          </Button>
        </div>

        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Transporter</TableHead>
                <TableHead className="font-semibold">Portal Link</TableHead>
                <TableHead className="font-semibold">Passcode</TableHead>
                <TableHead className="font-semibold text-center">Active</TableHead>
                <TableHead className="font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No portal links yet</TableCell></TableRow>
              ) : links.map((l) => (
                <TableRow key={l.link_id} data-testid={`portal-link-row-${l.link_id}`}>
                  <TableCell className="font-medium text-[#1A1F2E]">{l.transport_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 max-w-[320px]">
                      <span className="text-xs text-gray-600 truncate">{portalUrl(l.token)}</span>
                      <button onClick={() => copy(portalUrl(l.token), `url-${l.link_id}`)} className="text-indigo-600 hover:text-indigo-800 shrink-0" data-testid={`portal-copy-url-${l.link_id}`} title="Copy link">
                        {copiedKey === `url-${l.link_id}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <a href={portalUrl(l.token)} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600 shrink-0" title="Open portal"><ExternalLink className="w-4 h-4" /></a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold tracking-wider text-[#1A1F2E]" data-testid={`portal-passcode-${l.link_id}`}>{l.passcode}</span>
                      <button onClick={() => copy(l.passcode, `pass-${l.link_id}`)} className="text-indigo-600 hover:text-indigo-800" title="Copy passcode">
                        {copiedKey === `pass-${l.link_id}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={!!l.active} onCheckedChange={(v) => handleToggle(l, v)} data-testid={`portal-toggle-${l.link_id}`} />
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleRegenerate(l)} className="text-amber-600 hover:bg-amber-50" data-testid={`portal-regenerate-${l.link_id}`} title="Regenerate link & passcode"><RotateCcw className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(l)} className="text-red-600 hover:bg-red-50" data-testid={`portal-delete-${l.link_id}`} title="Delete link"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
};

export default TransporterPortalAdmin;
